<template>
    <b-container id="dashboard" style="margin-top:40px">
        <div class="banner-management">
            <div v-if="user.role === 'admin'" class="banner-management__button-container">
                <i class="banner-management__button material-icons" @click="goToBannerManagement">
                    settings
                </i>
            </div>
            <BannerCarousel />
        </div>
        <div class="section-title">
            <div class="section-title__text">
                Resumen de Actividad
            </div>
            <div class="section-title__filters">
                <template v-if="user.role === 'admin'">
                    <b-form-datepicker v-model="filters.from" label-no-date="Desde" placeholder="Desde"
                        :date-format-options="{ 'year': 'numeric', 'month': 'numeric', 'day': 'numeric' }"
                        class="filter-date" />
                    <b-form-datepicker v-model="filters.to" label-no-date="Hasta" placeholder="Hasta"
                        :date-format-options="{ 'year': 'numeric', 'month': 'numeric', 'day': 'numeric' }"
                        class="filter-date" />
                    <v-select v-model="filters.storeId" :options="storeOptions" :reduce="store => store.value"
                        placeholder="Todas las Tiendas" class="filter-store" />
                </template>
                <template v-else>
                    <b-form-select v-model="selectedRange" :options="rangeOptions" class="filter-range" />
                </template>
            </div>
        </div>

        <ShippingStatusCounters />

        <DeliveryStatsSection style="margin-top: 1.5rem" />

        <CustomerStatsSection style="margin-top: 1.5rem" />

        <div class="charts-section">
            <CommuneChartSection />
            <MonthlyChartSection />
        </div>
    </b-container>
</template>

<script>
import BannerCarousel from '../components/dashboard/BannerCarousel.vue';
import ShippingStatusCounters from '../components/dashboard/ShippingStatusCounters.vue';
import DeliveryStatsSection from '../components/dashboard/DeliveryStatsSection.vue';
import CustomerStatsSection from '../components/dashboard/CustomerStatsSection.vue';
import CommuneChartSection from '../components/dashboard/CommuneChartSection.vue';
import MonthlyChartSection from '../components/dashboard/MonthlyChartSection.vue';
import { mapGetters, mapActions } from 'vuex';
import { subtractDays } from '../utils/helpers';

export default {
    name: 'DashboardView',
    components: {
        BannerCarousel,
        ShippingStatusCounters,
        DeliveryStatsSection,
        CustomerStatsSection,
        CommuneChartSection,
        MonthlyChartSection,
    },
    data() {
        return {
            selectedRange: 7,
            rangeOptions: [
                { value: 1, text: 'Último día' },
                { value: 7, text: 'Últimos 7 días' },
                { value: 30, text: 'Últimos 30 días' }
            ],
            filters: {
                from: subtractDays(new Date(), 7),
                to: new Date().toISOString().split('T')[0],
                storeId: null
            }
        }
    },
    computed: {
        ...mapGetters(['user', 'stores']),
        storeOptions() {
            const stores = []
            stores.push({ value: null, label: 'Todas las Tiendas' })
            if (this.stores) {
                this.stores.forEach(store => {
                    stores.push({ value: store.id, label: store.name })
                })
            }
            return stores
        }
    },
    methods: {
        ...mapActions(['fetchDashboardStateCounters', 'fetchDeliveryTimeStats', 'fetchTopFrequentCustomers', 'fetchRepurchaseStats', 'fetchDeliveriesByCommune', 'fetchMonthlyDeliveries', 'fetchTopStoresByDeliveries', 'listStore']),
        goToBannerManagement() {
            this.$router.push({ name: 'Banners' });
        },
        async loadStateCounters() {
            const params = {
                from: this.filters.from,
                to: this.filters.to
            }
            if (this.filters.storeId) {
                params.storeId = this.filters.storeId
            }
            this.$store.commit('SET_DASHBOARD_FILTERS', { filters: params })
            const promises = [
                this.fetchDashboardStateCounters(params),
                this.fetchDeliveryTimeStats(params),
                this.fetchTopFrequentCustomers(params),
                this.fetchRepurchaseStats(params),
                this.fetchDeliveriesByCommune(params),
                this.fetchMonthlyDeliveries(params)
            ]
            if (this.user.role === 'admin') {
                promises.push(this.fetchTopStoresByDeliveries(params))
            }
            await Promise.all(promises)
        }
    },
    watch: {
        'filters.from': function () {
            this.loadStateCounters()
        },
        'filters.to': function () {
            this.loadStateCounters()
        },
        'filters.storeId': function () {
            this.loadStateCounters()
        },
        selectedRange(days) {
            this.filters.from = subtractDays(new Date(), days)
            this.filters.to = new Date().toISOString().split('T')[0]
        }
    },
    mounted() {
        if (this.user && this.user.role === 'admin') {
            this.listStore()
        }
        this.loadStateCounters()
    }
}
</script>

<style scoped>
@font-face {
    font-family: "Geist";
    src: url("/fonts/Geist-Regular.woff2") format("woff2");
    font-weight: 400;
    font-style: normal;
    font-display: swap;
}

#dashboard {
    font-family: "Geist Fallback", sans-serif;
}

.banner-management {
    position: relative;
}

.banner-management__button {
    text-align: right;
    font-size: 20px;
    color: #fff;
    cursor: pointer;
    background-color: #00000062;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.section-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 1.5rem;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.section-title__filters {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
}

.filter-date {
    width: 150px;
}

.filter-store {
    min-width: 200px;
    background-color: white;
}

.filter-range {
    width: 200px;
}

@media (max-width: 768px) {
    .section-title {
        flex-direction: column;
        align-items: flex-start;
    }

    .section-title__filters {
        flex-direction: column;
        width: 100%;
        gap: 0.5rem;
    }

    .filter-date,
    .filter-store,
    .filter-range {
        width: 100% !important;
        min-width: 100% !important;
    }
}

.banner-management__button-container {
    margin-bottom: 10px;
    display: flex;
    justify-content: flex-end;
}

.section-title__text {
    font-size: 20px;
    font-weight: 600;
}

.charts-section {
    margin-top: 1rem;
    padding-bottom: 1rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.charts-section>* {
    height: 100%;
}

@media (max-width: 768px) {
    .charts-section {
        grid-template-columns: 1fr;
    }
}
</style>